const cleanMessage = (value) => (typeof value === 'string' ? value.trim() : '');

const collectErrorMessages = (value, messages, depth) => {
  if (depth > 6 || value == null) return;

  if (typeof value === 'string') {
    const text = cleanMessage(value);
    if (text) messages.push(text);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectErrorMessages(item, messages, depth + 1));
    return;
  }

  if (typeof value === 'object') {
    const priorityFields = [
      'message',
      'error',
      'error_description',
      'detail',
      'description',
      'title',
      'reason',
    ];
    priorityFields.forEach((field) => {
      if (value[field]) {
        collectErrorMessages(value[field], messages, depth + 1);
      }
    });

    if (value.violations) collectErrorMessages(value.violations, messages, depth + 1);
    if (value.errors) collectErrorMessages(value.errors, messages, depth + 1);
    if (value.constraints) collectErrorMessages(value.constraints, messages, depth + 1);
    if (value.children) collectErrorMessages(value.children, messages, depth + 1);
  }
};

const getResultErrors = (result) => {
  const messages = [];

  if (result) {
    const hasExplicitMessage =
      cleanMessage(result.message) ||
      cleanMessage(result.error) ||
      cleanMessage(result.error_description) ||
      cleanMessage(result.detail);

    // Prefer business errors when the API gives one.
    if (result.message) collectErrorMessages(result.message, messages, 0);
    if (result.error) collectErrorMessages(result.error, messages, 0);
    if (result.error_description) collectErrorMessages(result.error_description, messages, 0);
    if (result.detail) collectErrorMessages(result.detail, messages, 0);

    // Only fallback to low-level parser errors when no explicit business error exists.
    if (!hasExplicitMessage && result.errors) {
      collectErrorMessages(result.errors, messages, 0);
    }
  }

  const uniqueMessages = Array.from(new Set(messages.filter((m) => typeof m === 'string' && m)));
  return uniqueMessages.length > 0 ? uniqueMessages : ['Erreur inconnue'];
};

const normalizeErrorMessageForDisplay = (message) => {
  const raw = cleanMessage(message);
  if (!raw) return 'Erreur inconnue';

  const withoutClientId = raw
    .replace(/\bfor\s+client\s*id\s*[:#-]?\s*\d+\s+and\s+/gi, 'for ')
    .replace(/\bclient\s*id\s*[:#-]?\s*\d+\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  return withoutClientId || raw;
};

const normalizeErrorMessageForGrouping = (message) =>
  normalizeErrorMessageForDisplay(message).toLowerCase().replace(/\s+/g, ' ').trim();

export const buildBulkUploadSummary = (results, selectedClients, total) => {
  const successCount = results.filter((r) => r.status === 'success').length;
  const failedResults = results.filter((r) => r.status !== 'success');
  const failedCount = failedResults.length;
  const failedClientIds = new Set(failedResults.map((r) => Number(r.clientId)));
  const failures = selectedClients.filter((client) => failedClientIds.has(Number(client.id)));
  const selectedClientsById = new Map(selectedClients.map((client) => [Number(client.id), client]));
  const errorGroupsMap = new Map();

  failedResults.forEach((result) => {
    const clientId = Number(result.clientId);
    const client = selectedClientsById.get(clientId) || {
      id: result.clientId,
      ext_code: String(result.clientId),
    };
    const resultErrors = getResultErrors(result);

    resultErrors.forEach((message) => {
      const groupMessage = normalizeErrorMessageForDisplay(message);
      const groupKey = normalizeErrorMessageForGrouping(message);
      if (!errorGroupsMap.has(groupKey)) {
        errorGroupsMap.set(groupKey, { message: groupMessage, clients: [] });
      }
      const group = errorGroupsMap.get(groupKey);
      const alreadyAdded = group.clients.find((groupClient) => Number(groupClient.id) === Number(client.id));
      if (!alreadyAdded) {
        group.clients.push(client);
      }
    });
  });

  return {
    total: results.length || total,
    successCount,
    failedCount,
    failures,
    errorGroups: Array.from(errorGroupsMap.values()),
  };
};

export const buildBulkUploadFailureSummary = (error, selectedClients, total) => {
  const message =
    (error && error.message) || (typeof error === 'string' ? error : 'Erreur lors de l\'import');
  return {
    total,
    successCount: 0,
    failedCount: total,
    failures: selectedClients,
    errorGroups: [{ message, clients: selectedClients }],
  };
};
